import {
  createDefaultCardFilters,
  type CardFilters,
} from "@-/dev.improve.haniverse.lib.cards.filter";

/** The deck's default filter state (same as the shared unrestricted defaults). */
export function createDeckDefaultCardFilters(): CardFilters {
  return createDefaultCardFilters();
}
import type {
  DeckDisplayMode,
  DeckSortMode,
} from "@-/dev.improve.haniverse.routes.nav.app.deck.entities";
import { useMemo, useState, type ReactNode } from "react";

export const TODAY_VIEW_VALUE = "today";
export const TODAY_KEY = "today";

export const UPCOMING_VIEW_VALUE = "upcoming";
export const PAST_VIEW_VALUE = "past";
export const INACTIVE_VIEW_VALUE = "inactive";

// Route keys. `upcoming` is served by `/decks/upcoming`; `past` is `/decks/past`; `inactive` is
// `/decks/inactive`.
export const UPCOMING_KEY = "upcoming";
export const PAST_KEY = "past";
export const INACTIVE_KEY = "inactive";

type ViewValue =
  | typeof TODAY_VIEW_VALUE
  | typeof INACTIVE_VIEW_VALUE
  | typeof PAST_VIEW_VALUE
  | typeof UPCOMING_VIEW_VALUE;

// Map a URL key (`upcoming` | `past` | `inactive`) to the internal `selectedView` value.
function resolveSelectedView(routeKey: string): ViewValue {
  if (routeKey === TODAY_KEY) {
    return TODAY_VIEW_VALUE;
  }
  if (routeKey === INACTIVE_KEY) {
    return INACTIVE_VIEW_VALUE;
  }
  if (routeKey === PAST_KEY) {
    return PAST_VIEW_VALUE;
  }
  return UPCOMING_VIEW_VALUE;
}

// Inverse of `resolveSelectedView`: turn the internal view value back into the URL key so
// navigation can build a `/decks/...` href.
export function selectedViewToRouteKey(selectedView: ViewValue): string {
  if (selectedView === TODAY_VIEW_VALUE) {
    return TODAY_KEY;
  }
  if (selectedView === INACTIVE_VIEW_VALUE) {
    return INACTIVE_KEY;
  }
  if (selectedView === PAST_VIEW_VALUE) {
    return PAST_KEY;
  }
  return UPCOMING_KEY;
}

export function useDeckLayoutContextValue({ routeKey }: { routeKey: string }) {
  const selectedView = useMemo(() => resolveSelectedView(routeKey), [routeKey]);
  const [sortMode, setSortMode] = useState<DeckSortMode>("due-soonest");
  // The user's pick wins. Until they make one, the open view may propose its own default (Upcoming
  // shows a short list as detailed tiles); otherwise cards show as glyph tiles.
  const [chosenDisplayMode, chooseDisplayMode] = useState<DeckDisplayMode | null>(null);
  const [viewDisplayMode, setViewDisplayMode] = useState<DeckDisplayMode | null>(null);
  const displayMode = chosenDisplayMode ?? viewDisplayMode ?? "morpheme-glyph-tiles";
  const setDisplayMode: (mode: DeckDisplayMode) => void = chooseDisplayMode;
  const [trailing, setTrailing] = useState<ReactNode>(null);
  const [cardFilters, setCardFilters] = useState<CardFilters>(createDeckDefaultCardFilters);

  return {
    cardFilters,
    displayMode,
    selectedView,
    setCardFilters,
    setDisplayMode,
    setSortMode,
    setTrailing,
    /** Proposes the open view's default display mode; `null` withdraws it. */
    setViewDisplayMode,
    sortMode,
    trailing,
  };
}

export type DeckLayoutContextValue = ReturnType<typeof useDeckLayoutContextValue>;
